import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  clearThemeDevFallbacks,
  subscribeThemeDevFallbacks,
  type ThemeDevFallbackEvent,
} from './themeDevOverlayStore'

/**
 * Dev-only overlay listing theme resolution fallbacks (override missing or failed import).
 * Renders nothing in production builds.
 */
export function ThemeDevOverlay() {
  const { t } = useTranslation()
  const [events, setEvents] = useState<ThemeDevFallbackEvent[]>([])

  useEffect(() => {
    if (!import.meta.env.DEV) {
      return
    }

    return subscribeThemeDevFallbacks(setEvents)
  }, [])

  if (!import.meta.env.DEV || events.length === 0) {
    return null
  }

  return (
    <aside
      data-testid="theme-dev-overlay"
      className="fixed bottom-3 left-3 z-[9999] max-h-[40vh] w-[min(100%-1.5rem,24rem)] overflow-auto rounded-md border border-amber-600/40 bg-amber-50 p-3 text-xs text-amber-950 shadow-lg"
      aria-live="polite"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="font-semibold">{t('theme.dev_overlay.title')}</p>
        <button
          type="button"
          className="rounded border border-amber-700/30 px-2 py-0.5 hover:bg-amber-100"
          onClick={() => clearThemeDevFallbacks()}
        >
          {t('theme.dev_overlay.dismiss')}
        </button>
      </div>
      <ul className="space-y-2">
        {events.map((event) => (
          <li key={event.id} className="rounded border border-amber-700/20 bg-white/70 p-2">
            <p>
              <span className="font-medium">{t('theme.dev_overlay.theme')}: </span>
              {event.themeSlug}
            </p>
            <p>
              <span className="font-medium">{t('theme.dev_overlay.source')}: </span>
              {event.sourcePath}
            </p>
            <p className="mt-1 break-words text-amber-900/80">{event.message}</p>
            {event.stack !== undefined && event.stack !== '' ? (
              <pre className="mt-1 max-h-24 overflow-auto whitespace-pre-wrap text-[10px] text-amber-900/70">
                {event.stack}
              </pre>
            ) : null}
          </li>
        ))}
      </ul>
    </aside>
  )
}
